import { useContext, useState, useMemo } from "react";
import {
  List,
  ListItemText,
  Collapse,
  ListItem,
  ListItemIcon,
  Menu,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import { makeStyles } from "@material-ui/core/styles";
import LinkIcon from "@material-ui/icons/Link";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import ClearIcon from "@material-ui/icons/Clear";
import SettingsIcon from "@material-ui/icons/Settings";
import { Wifi as WifiIcon } from "@material-ui/icons";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import BlockIcon from "@material-ui/icons/Block";
import PermPhoneMsgIcon from "@material-ui/icons/PermPhoneMsg";
import RefreshIcon from "@material-ui/icons/Refresh";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import DriveEtaIcon from "@material-ui/icons/DriveEta";
import FitnessCenterIcon from "@material-ui/icons/FitnessCenter";
import SearchIcon from "@material-ui/icons/Search";
import { DashboardContext } from "../Contexts/DashboardContext";
import { UserContext } from "../Contexts/UserContext";
import IncidentSearch from "./IncidentSearch";
import PersonSearch from "./PersonSearch";
import TicketSearch from "./TicketSearch/TicketSearch";
import PersonIcon from "@material-ui/icons/Person";
import DescriptionIcon from "@material-ui/icons/Description";
import ConfirmationNumberIcon from "@material-ui/icons/ConfirmationNumber";
import ReceiptIcon from "@material-ui/icons/Receipt";
import { checkPermission, checkRoles } from "../utils/Roles";
import VehicleSearch from "./VehicleSearch";
import Preferences from "./Preferences";
import { EXTERNAL_LINKS } from "../Data/ExternalLinks";
import { indexedDbTableNames, db as indexedDb } from "../IndexedDb/database";
import { clearAllStoredData } from "../IndexedDb/tableFunctions";
import { clearAllCookies, localGetData } from "../utils/utils";
import * as Sentry from "@sentry/react";
import { SetupContext } from "../Contexts/SetupContext";
import SizeAndWeightSearchContainer from "./SizeAndWeightFrame/Components/SizeAndWeightSearch/SizeAndWeightSearchContainer";
import useSearch from "./SizeAndWeightFrame/Components/SizeAndWeightSearch/useSearch";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
  },
  MenuList: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  hover: {
    color: "white",
    "&:hover": {
      color: "#d9833b",
    },
  },
}));

const deleteAllLocalData = async logOut => {
  if (
    window.confirm(
      "This will delete all your locally stored information and will require you to log in again. Please do not do this if you are currently offline. Are you sure you want to do this?"
    )
  ) {
    if (!logOut("CLIENT_STORAGE_RESET")) return null;
    const stillLoggedOut = () => !localGetData("mp_token");
    try {
      const cleared = await clearAllStoredData(indexedDbTableNames, indexedDb, stillLoggedOut);
      if (cleared === false || !stillLoggedOut()) return null;
    } catch (error) {
      console.error("Could not clear the local cache during logout.", error);
      Sentry.captureException(error, { tags: { operation: "clearLocalData" } });
      return null;
    }
    clearAllCookies();
    localStorage.clear();
    window.location.reload();
  }
  return null;
};

export default function HamburgerMenu({
  showVehicleSearch,
  setShowVehicleSearch,
  displayLogOut,
  canEdit = false,
  openShowPasswordChange,
  handleCancelIncident,
  inactive,
  handleIncidentOpening,
  handlePersonOpening,
  handleTicketOpening,
  handleVehicleOpening,
  setShowQueryModal,
  incidentOriId,
  incidentId,
  handleAddPersonMatchToIncidentState,
  handleAddVehicleMatchToIncidentState,
  tab = null,
  setTab = null,
  userPreferences,
  setUserPreferences,
  incidentPeople = [],
  subscriptionServer,
  setPausePolling = () => {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const classes = useStyles();
  const [listOpen, setListOpen] = useState(false);
  const [searchExpansion, setSearchExpansion] = useState(false);

  const [showPreferences, setShowPreferences] = useState(false);
  const [showIncidentSearch, setShowIncidentSearch] = useState(false);
  const [showPersonSearch, setShowPersonSearch] = useState(false);
  const [showTicketSearch, setShowTicketSearch] = useState(false);
  const [showSizeAndWeightSearch, setShowSizeAndWeightSearch] = useState(false);

  const { getSetup } = useContext(SetupContext);
  const { logOut, user, onlineState, sizeAndWeightEnabledForUser, isTesting } =
    useContext(UserContext);
  const {
    showActiveIncidents,
    setShowActiveIncidents,
    _DashboardContextIsMounted,
  } = useContext(DashboardContext);

  const canCancelIncident = useMemo(
    () => checkRoles(user?.roleNames, "cancel_incident"),
    [user]
  );

  const handleListOpen = () => {
    setListOpen(!listOpen);
  };
  const handleSearchExpansion = () => {
    setSearchExpansion(!searchExpansion);
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setListOpen(false);
    setSearchExpansion(false);
  };

  const handleOpenQueryModal = () => {
    setShowQueryModal(true);
  };

  const handleShowInactiveIncidents = e => {
    e.preventDefault();
    _DashboardContextIsMounted.current &&
      setShowActiveIncidents(!showActiveIncidents);
    handleClose(tab === 0 ? false : true, 0);
    if (tab !== 0 && setTab) {
      setTab(0);
    }
  };

  function openPreferences(e) {
    e.preventDefault();
    setShowPreferences(true);
    handleClose();
  }

  function refetchSetupLists(e) {
    e.preventDefault();
    const matchingKeys = Object.keys(localStorage).filter(key =>
      /^specific-setup-\d+$/.test(key)
    );
    matchingKeys.forEach(key => localStorage.removeItem(key));
    getSetup();
  }

  return (
    <div>
      <Tooltip title="Menu" placement="top">
        <IconButton
          color="inherit"
          aria-label="more"
          aria-controls="long-menu"
          aria-haspopup="true"
          onClick={handleClick}
          data-cy="user-options-button"
        >
          <MenuIcon className={classes.hover} style={{ fontSize: "2.5vw" }} />
        </IconButton>
      </Tooltip>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        // keepMounted
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            // maxHeight: ITEM_HEIGHT * 4.5,
            width: 200,
          },
        }}
        classes={{ list: classes.MenuList }}
        data-cy="user-options-menu"
      >
        <List
          component="nav"
          aria-labelledby="nested-list-subheader"
          className={classes.root}
          classes={{ padding: classes.MenuList }}
          data-cy="user-options-menu-list"
        >
          <ListItem
            button
            data-cy="search-links-button"
            disabled={!onlineState}
            onClick={handleSearchExpansion}
          >
            <ListItemIcon>
              <SearchIcon />
            </ListItemIcon>
            <ListItemText primary="Search for" />
            {searchExpansion ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse
            data-cy="search-links-collapse"
            in={searchExpansion}
            timeout="auto"
            unmountOnExit
          >
            <List data-cy="link-list" component="div" disablePadding>
              <ListItem
                button
                id="incident-search-button"
                onClick={() => {
                  handleClose();
                  setShowIncidentSearch(true);
                }}
                className={classes.nested}
              >
                <ListItemIcon>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary="Incidents" />
              </ListItem>
              <ListItem
                button
                id="people-search-button"
                onClick={() => {
                  handleClose();
                  setShowPersonSearch(true);
                }}
                className={classes.nested}
              >
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary="People" />
              </ListItem>
              <ListItem
                button
                id="ticket-search-button"
                onClick={() => {
                  handleClose();
                  setShowTicketSearch(true);
                }}
                className={classes.nested}
              >
                <ListItemIcon>
                  <ConfirmationNumberIcon />
                </ListItemIcon>
                <ListItemText primary="Tickets" />
              </ListItem>
              <ListItem
                button
                id="vehicle-search-button"
                onClick={() => {
                  handleClose();
                  setShowVehicleSearch(true);
                }}
                className={classes.nested}
              >
                <ListItemIcon>
                  <DriveEtaIcon />
                </ListItemIcon>
                <ListItemText primary="Vehicles" />
              </ListItem>
              {sizeAndWeightEnabledForUser && (
                <ListItem
                  button
                  id="size-and-weight-search-button"
                  onClick={() => {
                    handleClose();
                    setShowSizeAndWeightSearch(true);
                  }}
                  className={classes.nested}
                >
                  <ListItemIcon>
                    <FitnessCenterIcon />
                  </ListItemIcon>
                  <ListItemText primary="Size And Weight" />
                </ListItem>
              )}
            </List>
          </Collapse>
          {displayLogOut && (
            <ListItem
              button
              data-cy="inactive-buttons"
              onClick={handleShowInactiveIncidents}
            >
              <ListItemIcon>
                {showActiveIncidents ? (
                  <HourglassEmptyIcon />
                ) : (
                  <PermPhoneMsgIcon />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  showActiveIncidents
                    ? "Show Inactive Incidents"
                    : "Show Active Incidents"
                }
              />
            </ListItem>
          )}

          <ListItem
            button
            data-cy="external-links-button"
            disabled={!onlineState}
            onClick={handleListOpen}
          >
            <ListItemIcon>
              <LinkIcon />
            </ListItemIcon>
            <ListItemText primary="External Links" />
            {listOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse
            data-cy="external-links-collapse"
            in={listOpen}
            timeout="auto"
            unmountOnExit
          >
            <List data-cy="link-list" component="div" disablePadding>
              {EXTERNAL_LINKS.map(link => {
                return (
                  <ListItem
                    key={link.id}
                    data-cy={link.id}
                    button
                    component="a"
                    target="_blank"
                    href={link.href}
                    className={classes.nested}
                  >
                    <ListItemText inset={true} primary={link.text} />
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          {!displayLogOut && (
            <ListItem
              disabled={!canEdit || !canCancelIncident}
              button
              data-cy="cancel-incident-button"
              onClick={() => {
                handleClose();
                handleCancelIncident();
              }}
            >
              <ListItemIcon>
                <BlockIcon />
              </ListItemIcon>
              <ListItemText
                primary={`${!inactive ? "Cancel" : "Restore"} Incident`}
              />
            </ListItem>
          )}
          {!displayLogOut &&
            checkPermission(
              user?.roleNames,
              "add_query_returns",
              user?.editableOriIds,
              incidentOriId,
              incidentId
            ) && (
              <ListItem
                disabled={!canEdit}
                button
                data-cy="run-query-button"
                onClick={() => {
                  handleClose();
                  handleOpenQueryModal();
                }}
              >
                <ListItemIcon>
                  <ReceiptIcon />
                </ListItemIcon>
                <ListItemText primary={`Run Query`} />
              </ListItem>
            )}
          <ListItem
            button
            data-cy="preferences-button"
            onClick={openPreferences}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Preferences" />
          </ListItem>
          {isTesting && (
            <ListItem
              button
              disabled={!onlineState}
              data-cy="setup-refetch-button"
              onClick={refetchSetupLists}
            >
              <ListItemIcon>
                <RefreshIcon />
              </ListItemIcon>
              <ListItemText primary="Refetch Setup Lists" />
            </ListItem>
          )}
          {!onlineState && (
            <ListItem
              button
              disabled={onlineState}
              data-cy="manual-reconnect-button"
              onClick={() => {
                subscriptionServer.manualReconnect();
              }}
            >
              <ListItemIcon>
                <WifiIcon />
              </ListItemIcon>
              <ListItemText primary="Reconnect" />
            </ListItem>
          )}
          {displayLogOut && (
            <ListItem
              button
              disabled={!onlineState}
              data-cy="delete-cookies-button"
              onClick={() => {
                handleClose();
                deleteAllLocalData(logOut);
              }}
            >
              <ListItemIcon>
                <ClearIcon />
              </ListItemIcon>
              <ListItemText primary="Clear cache" />
            </ListItem>
          )}
          {displayLogOut && (
            <ListItem
              button
              data-cy="password-change-button"
              onClick={() => {
                handleClose();
                openShowPasswordChange();
              }}
            >
              <ListItemIcon>
                <VpnKeyIcon />
              </ListItemIcon>
              <ListItemText primary="Set Password" />
            </ListItem>
          )}
          {displayLogOut && (
            <ListItem button data-cy="logout-button" onClick={() => logOut("USER_REQUESTED")}>
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          )}
        </List>
      </Menu>
      <Preferences
        showPreferences={showPreferences}
        closePreferences={() => setShowPreferences(false)}
        userId={user?.id}
        userPreferences={userPreferences}
        setUserPreferences={setUserPreferences}
      />
      <IncidentSearch
        showModal={showIncidentSearch}
        setShowModal={setShowIncidentSearch}
        handleIncidentOpening={handleIncidentOpening}
        setPausePolling={setPausePolling}
      />
      <PersonSearch
        showModal={showPersonSearch}
        setShowModal={setShowPersonSearch}
        handlePersonOpening={handlePersonOpening}
        userRoles={user?.roleNames}
        incidentId={incidentId}
        handleAddPersonMatchToIncidentState={
          handleAddPersonMatchToIncidentState
        }
        incidentPeople={incidentPeople}
        setPausePolling={setPausePolling}
      />
      {showTicketSearch && (
        <TicketSearch
          showModal={showTicketSearch}
          setShowModal={setShowTicketSearch}
          handlePersonOpening={handlePersonOpening}
          handleIncidentOpening={handleIncidentOpening}
          handleTicketOpening={handleTicketOpening}
        />
      )}
      <VehicleSearch
        showModal={showVehicleSearch}
        setShowModal={setShowVehicleSearch}
        handleIncidentOpening={handleIncidentOpening}
        handleVehicleOpening={handleVehicleOpening}
        userRoles={user?.roleNames}
        incidentId={incidentId}
        handleAddVehicleMatchToIncidentState={
          handleAddVehicleMatchToIncidentState
        }
      />
      {showSizeAndWeightSearch && (
        <SizeAndWeightSearchContainer
          handleIncidentOpening={handleIncidentOpening}
          handleTicketOpening={handleTicketOpening}
          showModal={showSizeAndWeightSearch}
          setShowModal={setShowSizeAndWeightSearch}
          useSearchHook={useSearch}
        />
      )}
    </div>
  );
}
